import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import { useUrlState } from "@/lib/useUrlState";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PocChip, PocChipPair, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import { RISK_META, RISK_FALLBACK } from "@/lib/risk";
import RulesInfoDialog from "@/components/RulesInfoDialog";
import { Crown, MagnifyingGlass, CaretRight, Warning } from "@phosphor-icons/react";
import ExportButton from "@/components/ExportButton";
import { ENTERPRISE_COLUMNS } from "@/lib/exportColumns";
import { formatLastSeen } from "@/lib/risk";
import FirmRowActions from "@/components/FirmRowActions";

const BUCKET_META = {
  Active:            { color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  "At Risk":         { color: "text-amber-700",   bg: "bg-amber-50 border-amber-200",     dot: "bg-amber-500" },
  Inactive:          { color: "text-orange-700",  bg: "bg-orange-50 border-orange-200",   dot: "bg-orange-500" },
  "Potential Churn": { color: "text-red-700",     bg: "bg-red-50 border-red-200",         dot: "bg-red-500" },
};
const BUCKET_ORDER = ["Potential Churn", "Inactive", "At Risk", "Active"];

export default function Enterprise() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUrlState("search", "");
  const [bucket, setBucket] = useUrlState("bucket", "all");

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/enterprise", {
        params: {
          search: search.trim() || undefined,
          bucket: bucket !== "all" ? bucket : undefined,
        },
      })
      .then((r) => {
        setCustomers(r.data.customers);
        setCounts(r.data.counts);
      })
      .finally(() => setLoading(false));
  }, [search, bucket]);

  const escalateCount = counts["Potential Churn"] || 0;
  const actionCount = counts.Inactive || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
          <Crown size={28} weight="fill" className="text-amber-500" />
          Enterprise Watchlist
        </h1>
        <p className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
          <span>Stricter monitoring of high-value Enterprise firms.</span>
          <RulesInfoDialog view="enterprise" label="the Enterprise watchlist" />
        </p>
      </div>

      {(escalateCount > 0 || actionCount > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="ent-alert-banner">
          <Warning size={20} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-red-900">
              {escalateCount > 0 && `${escalateCount} firm${escalateCount > 1 ? "s" : ""} at potential churn`}
              {escalateCount > 0 && actionCount > 0 && " · "}
              {actionCount > 0 && `${actionCount} firm${actionCount > 1 ? "s" : ""} inactive`}
            </div>
            <div className="text-xs text-red-800 mt-0.5">
              Open them below — stricter thresholds apply than the main Dashboard.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <button
          type="button"
          onClick={() => setBucket("all")}
          className={`bg-white border rounded-lg p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm ${
            bucket === "all" ? "border-gray-900 ring-2 ring-gray-900/10" : "border-gray-200"
          }`}
          data-testid="ent-card-all"
          aria-pressed={bucket === "all"}
        >
          <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            All Enterprise
          </div>
          <div className="mt-2 font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
            {BUCKET_ORDER.reduce((sum, b) => sum + (counts[b] || 0), 0)}
          </div>
        </button>
        {BUCKET_ORDER.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => setBucket(bucket === b ? "all" : b)}
            className={`bg-white border rounded-lg p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm ${
              bucket === b ? "border-gray-900 ring-2 ring-gray-900/10" : "border-gray-200"
            }`}
            data-testid={`ent-card-${b.replace(/\s+/g, "-")}`}
            aria-pressed={bucket === b}
          >
            <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full ${BUCKET_META[b].dot}`} />
              {b}
            </div>
            <div className="mt-2 font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
              {counts[b] || 0}
            </div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search firm, subdomain, city, POC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="ent-search"
          />
        </div>
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger className="w-[180px]" data-testid="ent-filter-bucket">
            <SelectValue placeholder="All buckets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All buckets</SelectItem>
            {BUCKET_ORDER.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-gray-500 ml-auto">
          {loading ? "Loading…" : `${customers.length} Enterprise firm${customers.length === 1 ? "" : "s"}`}
        </div>
        <ExportButton
          filename={`enterprise-${bucket === "all" ? "all" : bucket.replace(/\s+/g, "-").toLowerCase()}`}
          sheetName="Enterprise"
          columns={ENTERPRISE_COLUMNS}
          rows={customers}
          testId="export-enterprise"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="ent-list">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left">
              <Th>Firm</Th><Th>Users</Th><Th>Cloud</Th><Th>Bucket</Th><Th>Last seen</Th><Th>POC</Th><th className="w-16"></th><th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && customers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">No Enterprise firms found.</td></tr>
            )}
            {!loading && customers.map((c) => {
              const meta = BUCKET_META[c.enterprise_bucket] || BUCKET_META.Active;
              const leftMeta = RISK_META[c.risk] || RISK_FALLBACK;
              return (
                <tr
                  key={c.subdomain}
                  onClick={() => navigate(`/firms/${c.subdomain}${firmFromQS(location)}`)}
                  className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${leftMeta.leftClass}`}
                  data-testid={`ent-row-${c.subdomain}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.firm_name}</div>
                    <FirmSubmeta firm={c} />
                  </td>
                  <td className="px-4 py-3"><UsersCell value={c.num_users} /></td>
                  <td className="px-4 py-3"><CloudPill plan={c.cloud_plan} /></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap ${meta.bg} ${meta.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      {c.enterprise_bucket}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatLastSeen(c.last_seen, c.days_since_seen)}
                  </td>
                  <td className="px-4 py-3"><PocChipPair poc={c.poc} salesPoc={c.sales_poc} /></td>
                  <td className="px-2 py-3 whitespace-nowrap"><FirmRowActions firm={c} /></td>
                  <td className="px-4 py-3 text-right"><CaretRight size={14} className="text-gray-300" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500">{children}</th>;
}

// Cloud-plan pill — shown only for Enterprise firms. Renders nothing if the
// firm has no cloud_plan set yet (e.g. a brand-new Enterprise firm that hasn't
// been classified). NEVER rendered for non-Enterprise tiers (server-side
// firm_to_out enforces cloud_plan === null for those).
export function CloudPill({ plan }) {
  if (!plan) return <span className="text-xs text-gray-300">—</span>;
  const isOwn = plan === "Own Cloud";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap ${
        isOwn
          ? "bg-violet-50 border-violet-200 text-violet-700"
          : "bg-sky-50 border-sky-200 text-sky-700"
      }`}
      data-testid={`cloud-pill-${isOwn ? "own" : "assureai"}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isOwn ? "bg-violet-500" : "bg-sky-500"}`} />
      {plan}
    </span>
  );
}
